import styles from "../../assets/scss/modules/PublicListingGrid.module.scss";
import styles2 from "../../assets/scss/modules/PublicListings.module.scss";
import React, {useEffect, useRef, useState} from 'react';
import {CtrDomainsVf, CtrInscriptions, CtrType_Stat} from "../../graphql/generated/graphql-main.tsx";
import Card from "@mui/material/Card";
import clsx from "clsx";
import {CardActionArea, Chip, Menu, MenuItem, Pagination, Select, useMediaQuery, useTheme} from "@mui/material";
import svgEvFavicon from "../../assets/images/ev-favicon.svg";
import svgMgFavicon from "../../assets/images/mcgill-logo.svg";
import imgPlaceholder from "../../assets/images/img-placeholder.jpg";
import {nodeIf} from "../../utils/react-utils.tsx";
import Zircon from "../global/Zircon.tsx";
import {
    IconBathroom,
    IconBedroom,
    IconLivingSpace,
    IconLotArea,
    IconParkingGarage
} from "../../assets/icons/components";
import {apiUrls, strEmpty} from "../../utils/utils.ts";
import {useWpContext} from "../context/WordPressProvider.tsx";
import {cfg} from "../../config.ts";
import useCtrGridListings from "../../hooks/useCtrGridListings.ts";
import useCtrValeursFixes from "../../hooks/useCtrValeursFixes.ts";
import useCtrMetaFields from "../../hooks/useCtrMetaFields.tsx";
import useCtrInscription from "../../hooks/useCtrInscription.tsx";
import useReactTexts from "../../hooks/useReactTexts.ts";
import {CopyAllRounded} from "@mui/icons-material";
import useSiteConfig from "../../hooks/useSiteConfig.ts";
import {PropertiesSortByOptions} from "../../@types/general.ts";

type Props = {
    listings: CtrInscriptions[];
    hasSearchResults: boolean;
    searchBar: React.ReactNode;
    toggleGroup: React.ReactNode;
    msgNoResults: React.ReactNode;
}
const PropertiesGridView = (props: Props) => {
    const {others} = useWpContext<ZListings>();
    const {__} = useReactTexts();
    const xtheme = useTheme();
    const isSm = useMediaQuery(xtheme.breakpoints.down("sm"));
    const gridViewRef = useRef<HTMLDivElement | null>(null);

    const {photoByMls, centrisPhoto, charByMls} = useCtrGridListings();
    const {fixedValue} = useCtrValeursFixes();
    const {toValue} = useCtrMetaFields();
    const {title, price, shortAddress, rawAddress} = useCtrInscription();
    const {$config} = useSiteConfig();

    const [sort, setSort] = useState<PropertiesSortByOptions>($config.default_grid_sort
        ?? PropertiesSortByOptions.UPDATED_DESC);
    const [page, setPage] = useState<number>(1);
    const [ctxCurrentListing, setCtxCurrentListing] = useState<CtrInscriptions | null>(null);
    const [ctxMenuAnchor, setCtxMenuAnchor] = useState<{ x: number, y: number } | null>(null);

    const perPage = others.per_page ?? cfg.react.grid_view.listings_per_page;
    const totalPages = Math.ceil(props.listings.length / perPage) ?? 1;
    const showRandom = !others.paginate && others.random;
    const showPagination = others.paginate && totalPages > 1;
    const showSorting = others.sorting && !props.hasSearchResults; // hide sorting when we have search results

    // sort results (except when these are search results)
    const sortedListings = props.listings.toSorted(propertySorter(sort));

    // shared sort function for properties (used in both filtered & random listings)
    function propertySorter(_sort: PropertiesSortByOptions) {
        return function (a: CtrInscriptions, b: CtrInscriptions) {
            // do NOT sort search results
            if (props.hasSearchResults) return 0;

            const priceA = parseFloat(a.PRIX_DEMANDE || a.PRIX_LOCATION_DEMANDE || "0");
            const priceB = parseFloat(b.PRIX_DEMANDE || b.PRIX_LOCATION_DEMANDE || "0");
            const modA = a.DATE_MODIF || "";
            const modB = b.DATE_MODIF || "";

            if (_sort == PropertiesSortByOptions.PRICE_ASC) {
                return priceA < priceB ? -1 : 1;
            } else if (_sort == PropertiesSortByOptions.PRICE_DESC) {
                return priceA > priceB ? -1 : 1;
            } else if (_sort == PropertiesSortByOptions.UPDATED_ASC) {
                return modA < modB ? -1 : 1;
            } else if (_sort == PropertiesSortByOptions.UPDATED_DESC) {
                return modA > modB ? -1 : 1;
            }
            return 0;
        }
    }

    // note: for now applies only to the address section to still allow right-click for 'Open link in new tab' ...
    const onCardContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, lst: CtrInscriptions) => {
        // in random mode, right-clicking causes full re-render, thus causing all listings to randomize again
        if (showRandom) return;

        // note: these operations trigger re-rendering, so be careful
        e.preventDefault();
        setCtxCurrentListing(lst);
        setCtxMenuAnchor({x: e.clientX + 2, y: e.clientY - 6});
    }
    const copyPropertyAddress = async () => {
        // note: always use raw Centris address (w/o apt. number) for matching w/ McGill project
        await navigator.clipboard.writeText(rawAddress(ctxCurrentListing));
        setCtxMenuAnchor(null);
    }
    const copyPropertyMlsId = async () => {
        await navigator.clipboard.writeText(ctxCurrentListing?.NO_INSCRIPTION!);
        setCtxMenuAnchor(null);
    }

    // randomly pick {@link perPage} listings based on a threshold price defined in Elementor widget.
    const randomListings = (): CtrInscriptions[] => {
        // 1 - only focus on properties above threshold price set in Elementor widget
        const aboveThreshold = sortedListings.filter(it => it.PRIX_DEMANDE != null
            && !strEmpty(it.PRIX_DEMANDE)
            && (parseInt(it.PRIX_DEMANDE) || -1) >= others.threshold_price);
        // + = not enough properties above threshold, 0 = sufficient properties above threshold
        const stillAvailable = Math.max(0, perPage - aboveThreshold.length);

        // 2 - if not enough properties above the specified threshold, fill in with other highest-price properties
        if (stillAvailable > 0) {
            // exclude the properties officially above the threshold
            const _usedIds = aboveThreshold.map(it => it.NO_INSCRIPTION!);
            // sort by descending price, exclude, then get the number of properties still available
            const complement = sortedListings
                .toSorted(propertySorter(PropertiesSortByOptions.PRICE_DESC))
                .filter(it => !_usedIds.includes(it.NO_INSCRIPTION!))
                .slice(0, stillAvailable);
            // push complement properties to same array, so we can use same random index getter below
            aboveThreshold.push(...complement);
        }

        // 3 - get 'per_page' random MLS IDs to pick from INSCRIPTIONS
        const final: CtrInscriptions[] = [];
        while (final.length < perPage && aboveThreshold.length > 0) {
            // pick any random index from entirety of above-threshold listings
            const randomIndex = Math.floor(Math.random() * aboveThreshold.length);
            const randomListing = aboveThreshold.splice(randomIndex, 1)[0]; // [0] may be null
            if (randomListing != null)
                final.push(randomListing);
        }

        return final;
    }

    // return number of listings per page
    const paginateListings = (page: number): CtrInscriptions[] => {
        const start = (page - 1) * perPage;
        const end = start + perPage;
        return sortedListings.slice(start, end);
    }

    // listings to show (either random mode or normal-order paginated or non-paginated list)
    const listings2show = () => {
        if (showRandom)
            return randomListings();
        else
            return paginateListings(page);
    }

    // when clicking on bottom pagination bar, always scroll up to top to view new listings
    const paginationAutoScrollUp = () => {
        gridViewRef.current?.scrollIntoView();
    }

    // listings pagination switcher
    const renderPagination = (position: "top" | "bottom") => {
        return <div className={"my-4 flex flex-nowrap justify-center items-center"}>
            <Pagination
                className={styles.pagination}
                color={"primary"}
                variant={"text"}
                shape={"circular"}
                siblingCount={isSm ? 0 : 2}
                showFirstButton
                showLastButton
                size={"medium"}
                count={totalPages}
                page={page}
                onChange={(event, page: number) => {
                    setPage(page);
                    position == "bottom" && paginationAutoScrollUp();
                }}
            ></Pagination>
        </div>
    }

    // custom sort options dropdown
    const renderSortOptions = () => {
        return <Select
            size={"small"}
            className={clsx("text-base lg:ml-8 sm:!w-60 !text-left")}
            MenuProps={{
                classes: {
                    root: styles2.popper_autocomplete,
                }
            }}
            value={sort}
            onChange={event => setSort(event.target.value as PropertiesSortByOptions)}
        >
            <MenuItem value={PropertiesSortByOptions.UPDATED_DESC}>
                {__("Plus récents")}
            </MenuItem>
            <MenuItem value={PropertiesSortByOptions.UPDATED_ASC}>
                {__("Moins récents")}
            </MenuItem>
            <MenuItem value={PropertiesSortByOptions.PRICE_ASC}>
                {__("Prix croissants")}
            </MenuItem>
            <MenuItem value={PropertiesSortByOptions.PRICE_DESC}>
                {__("Prix décroissants")}
            </MenuItem>
        </Select>
    }

    // full property card inside the grid
    const renderPropertyCard = (lst: CtrInscriptions, idx: number) => {
        const mlsId = lst.NO_INSCRIPTION;
        const value = toValue<CtrInscriptions>(lst);
        const isBrokerPage = !strEmpty(others.broker_id);

        // When viewing specific broker's properties (in 'Nos conseillers' section), we must use the hub redirection
        // mechanism which properly handles commercial listings which are not present on main EV Quebec website.
        // Otherwise, clicking a property would try to open it in main EV Quebec site, but it would fall back to the
        // 'All properties' page since EV Quebec site does NOT list commercial listings. This is not an issue on the
        // main Grid View since all listings there are available in this site. (https://mls-hub.evquebec.com/mls/fr/14654598)
        const url = isBrokerPage
            ? `${apiUrls.rest()}/mls/${window.zdata.lang}/${mlsId}` // never put any trailing slash (/)
            : `${others.url_listing_single}${mlsId}/`;

        return <Card
            key={`card-${mlsId}`}
            className={clsx("property card relative block h-full rounded-none", styles.card__property)}
        >

            {/* that's what makes the whole card clickable - note: trailing slash is important */}
            <CardActionArea
                href={url}
                rel={isBrokerPage ? "nofollow" : undefined}
                target={$config.listing_target_self ? "_self" : "_blank"}
            >

                {/* show 'Sold' tag ONLY if sold (do NOT show other status) */}
                {lst?.CODE_STATUT == "VE" && <Chip
                    color={"error"}
                    className={"rounded absolute left-4 top-4 !z-10"}
                    label={fixedValue(CtrDomainsVf.CodeStatut, lst?.CODE_STATUT)}
                ></Chip>}

                {/* display MLS ID in all listings */}
                {/*<Chip*/}
                {/*    className={clsx("absolute right-4 top-4 !z-10", styles.chip__mls)}*/}
                {/*    label={`# ${lst?.NO_INSCRIPTION}`}*/}
                {/*></Chip>*/}

                {/* todo dev only - broker #1, office #1 */}
                {/*<Chip*/}
                {/*    className={clsx("absolute right-4 top-14 !z-10", styles.chip__mls)}*/}
                {/*    label={`C ${lst?.COURTIER_INSCRIPTEUR_1}`}*/}
                {/*></Chip>*/}
                {/*<Chip*/}
                {/*    className={clsx("absolute right-4 top-[6rem] !z-10", styles.chip__mls)}*/}
                {/*    label={`B ${lst?.BUREAU_INSCRIPTEUR_1}`}*/}
                {/*></Chip>*/}
                {/* todo dev only */}

                {/* property thumbnail */}
                {/* note: line-height:0 is required to remove that weird blank space between img & div below */}
                <div className={"card__thumbnail h-auto aspect-[3/2] hover:opacity-75 group relative leading-[0px]"}>
                    {/* E&V mini-logo on each card */}
                    <img className={"absolute right-3 bottom-3 !z-10 w-10"}
                         src={$config.is_mcgill ? svgMgFavicon : svgEvFavicon}
                         alt={`Watermark`}
                    ></img>

                    {/* clickable image to open full listing page -- full url is provided by WP */}
                    {/* note: use !important for height: 100%; else Elementor messes up img height */}
                    <img src={photoByMls(mlsId) ?? ""} alt={""}
                         loading={idx === 0 ? "eager" : "lazy"}
                         fetchPriority={idx === 0 ? "high" : undefined}
                         className={"!h-full w-full object-cover"}
                         onContextMenu={event => event.preventDefault()}
                         onError={({currentTarget}) => {
                             currentTarget.onerror = null; // prevents looping
                             currentTarget.src = centrisPhoto(mlsId) ?? imgPlaceholder;
                         }}
                    ></img>
                </div>

                {/* property details */}
                <div className={"p-[15px]"} onContextMenu={e => onCardContextMenu(e, lst)}>
                    {/* summary of the property */}
                    <div className={"flex justify-between card__summary1 py-1"}>
                        {/* price */}
                        <div className={"card__price flex flex-wrap items-baseline gap-x-1"}>
                            {price(lst)}
                            {lst?.CODE_STATUT == "VE" && (
                                <span className={"text-xs"}>{__("Prix demandé")}</span>
                            )}
                        </div>
                        {/* number of rooms, bathrooms - note: this is now at bottom of card */}
                        {/*<div className={clsx("flex card__rooms space-x-4", styles.card__icons)}>*/}
                        {/*    <div className={"flex items-center space-x-2"}>*/}
                        {/*        <Zircon component={IconBedroom}/>*/}
                        {/*        <span>{value("NB_CHAMBRES") ?? "nd"}</span>*/}
                        {/*    </div>*/}
                        {/*    <div className={"flex items-center space-x-2"}>*/}
                        {/*        <Zircon component={IconBathroom}/>*/}
                        {/*        <span>{value("NB_SALLES_BAINS") ?? "nd"}</span>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                    </div>

                    {/* type of property (catchy title) */}
                    <div className={"card__summary2 flex flex-wrap justify-between space-x-2"}>

                        {/* type of property (genre) */}
                        <div className={"font-semibold"}>
                            {title(lst)}
                        </div>

                        {/* category (higher-level definition) */}
                        {/*<div>*/}
                        {/*    {value("CATEGORIE_PROPRIETE", {domain: CtrDomainsVf.CategoriePropriete})}*/}
                        {/*</div>*/}
                    </div>

                    {/* address - note: for now only show municipality, neither borough nor region */}
                    <div className={"card__summary3"}>
                        {shortAddress(lst)}
                    </div>

                    {/* icons showing key aspects of the property */}
                    <div className={clsx("card__summary4 flex flex-wrap justify-start gap-x-4 gap-y-2 " +
                        "mx-auto pt-2", styles.card__icons)}>

                        {/* number of bedrooms */}
                        {nodeIf(value("NB_CHAMBRES"), val => (
                            <div className={"flex items-center space-x-1"}>
                                <Zircon component={IconBedroom}/><span>{val}</span>
                            </div>
                        ))}

                        {/* combine total bathrooms & powder rooms under one icon */}
                        {(() => {
                            const bathrooms = parseInt(value("NB_SALLES_BAINS") ?? '0');
                            const washrooms = parseInt(value("NB_SALLES_EAU") ?? '0');
                            const total = bathrooms + washrooms;

                            // e.g. 3+2, 7, etc. -- do NOT show +X if only bathrooms and no washrooms
                            let label: string;
                            if ([bathrooms, washrooms].every(it => it > 0))
                                label = `${bathrooms}+${washrooms}`;
                            else label = `${bathrooms || washrooms}`;

                            if (total > 0) {
                                return <div className={"flex items-center space-x-1"}>
                                    <Zircon component={IconBathroom}/><span>{label}</span>
                                </div>
                            }
                        })()}

                        {/* garage parking */}
                        {nodeIf(charByMls(lst?.NO_INSCRIPTION, CtrType_Stat.Gara)?.NOMBRE, val => (
                            <div className={"flex items-center space-x-1"}>
                                <Zircon component={IconParkingGarage}/><span>{val}</span>
                            </div>
                        ))}

                        {/* outdoor parking */}
                        {/*{nodeIf(charByMls(lst?.NO_INSCRIPTION, CtrType_Stat.Ext)?.NOMBRE, val => (*/}
                        {/*    <div className={"flex items-center space-x-1"}>*/}
                        {/*        <Zircon component={IconParkingOutdoor}/><span>{val}</span>*/}
                        {/*    </div>*/}
                        {/*))}*/}

                        {/* number of rooms */}
                        {/*{nodeIf(value("NB_PIECES") ?? value("NB_UNITES_TOTAL"), val => (*/}
                        {/*    <div className={"flex items-center space-x-1"}>*/}
                        {/*        <Zircon component={IconRoomCount}/><span>{val}</span>*/}
                        {/*    </div>*/}
                        {/*))}*/}

                        {/* living area */}
                        {nodeIf(value("SUPERFICIE_HABITABLE", {type: "numeric"}), val => (
                            <div className={"flex items-center space-x-1"}>
                                <Zircon component={IconLivingSpace}/>
                                <span>{val}{' '}{lst?.UM_SUPERFICIE_HABITABLE?.toLowerCase()}</span>
                            </div>
                        ))}

                        {/* lot area */}
                        {nodeIf(value("SUPERFICIE_TERRAIN", {type: "numeric"}), val => (
                            <div className={"flex items-center space-x-1"}>
                                <Zircon component={IconLotArea}/>
                                <span>{val}{' '}{lst?.UM_SUPERFICIE_TERRAIN?.toLowerCase()}</span>
                            </div>
                        ))}

                        {/* construction year */}
                        {/*{nodeIf(value("ANNEE_CONTRUCTION") ?? value("CODE_ANNEE_CONSTRUCTION", {*/}
                        {/*    domain: CtrDomainsVf.CodeAnneeConstruction*/}
                        {/*}), val => (*/}
                        {/*    <div className={"flex items-center space-x-1"}>*/}
                        {/*        <Zircon component={IconConstructionYear}/><span>{val}</span>*/}
                        {/*    </div>*/}
                        {/*))}*/}

                    </div>
                </div>
            </CardActionArea>
        </Card>
    }

    // when 'listings' dataset changes, reset page to 1 in case user was at a page number greater than new dataset
    useEffect(() => {
        setPage(1);
    }, [props.listings]);

    // main wrapper section
    return <div
        ref={gridViewRef}
        id={'x-grid-properties'}
        className={"scroll-mt-32 md:scroll-mt-44"}
    >
        {/* show pagination module */}
        {showPagination && renderPagination("top")}

        {/* search bar, sort options, and view toggle group */}
        <div className={clsx("my-4 flex flex-wrap md:flex-nowrap items-center justify-between space-y-4 " +
            "md:space-y-0 md:space-x-4", styles2.dd_select)}>
            <div className={"w-full justify-start"}>
                {props.searchBar}
            </div>

            <div className={"w-full md:w-auto flex justify-end space-x-4"}>
                {/* show sort options only if 'show-sorting' is enabled */}
                {showSorting && renderSortOptions()}
                {/* show view toggler at all times */}
                {props.toggleGroup}
            </div>
        </div>

        {/* only appears if there are no results */}
        {props.msgNoResults}

        {/* loop over set of listings depending on filtering / sorting criteria */}
        <section className={"mb-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-20 gap-x-10"}>
            {listings2show().map((lst, idx) => renderPropertyCard(lst, idx))}
        </section>

        {showPagination && renderPagination("bottom")}

        {/* context menu (right-click) for property card */}
        {/* note: <Menu> = <Modal>, so it create fullscreen overlay; maybe consider Popper + ClickAwayListener */}
        <Menu
            open={ctxMenuAnchor != null}
            onClose={e => setCtxMenuAnchor(null)}
            classes={{paper: "rounded-none", list: "p-0 *:text-sm *:px-2"}}
            anchorReference={"anchorPosition"}
            anchorPosition={ctxMenuAnchor == null ? undefined : {
                top: ctxMenuAnchor.y, left: ctxMenuAnchor.x
            }}
        >
            <MenuItem onClick={copyPropertyAddress}>
                <CopyAllRounded fontSize={"small"}/> &nbsp;Adresse / Address
            </MenuItem>
            <MenuItem onClick={copyPropertyMlsId}>
                <CopyAllRounded fontSize={"small"}/> &nbsp;# Inscription
            </MenuItem>
        </Menu>
    </div>
};

export default PropertiesGridView;
